import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/Api/axiosV2Config';

export interface PaymentModeAggregation {
  count: number;
  percentage: number;
  factureTtc: number;
  factureHt: number;
  encaisseTtc: number;
  encaisseHt: number;
  aEncaisserTtc: number;
  aEncaisserHt: number;
}

export interface VendorFinancialCompiledData {
  venteContrats: {
    total: PaymentModeAggregation;
    inclusion: PaymentModeAggregation;
    comptant: PaymentModeAggregation;
    comptantAgence: PaymentModeAggregation;
    mensualise: PaymentModeAggregation;
  };
  refacturation: {
    total: PaymentModeAggregation;
    garantiesIncluses: PaymentModeAggregation;
    garantiesPayantesOffertes: PaymentModeAggregation;
    garantiesPayantesAvecDecote: PaymentModeAggregation;
  };
  retrocession: {
    total: PaymentModeAggregation;
  };
  fraisStripe: {
    total: PaymentModeAggregation;
    client: PaymentModeAggregation;
    agence: PaymentModeAggregation;
  };
  prestationAffinicar: {
    total: PaymentModeAggregation;
  };
  priseEnChargeSav: {
    total: PaymentModeAggregation;
    garantie: PaymentModeAggregation;
    commercial: PaymentModeAggregation;
  };
  bilan: PaymentModeAggregation;
}

// ═══════════════════════════════════════════════════════════════
// Hook: useVendorDetails
// ═══════════════════════════════════════════════════════════════

export function useVendorDetails(societyId: string) {
  return useQuery({
    queryKey: ['vendor-details', societyId],
    queryFn: async (): Promise<{ uuid: string; name: string; code: string }> => {
      const { data } = await axiosInstance.get(`/pro-v2/societies/${societyId}/details`);
      return data;
    },
    enabled: !!societyId,
    staleTime: 5 * 60 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════
// Hook: useFinancialSummary
// ═══════════════════════════════════════════════════════════════

interface FinancialSummaryFilters {
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  paymentMode?: string;
  storeId?: string;
  filterUserId?: string;
  page?: number;
  limit?: number;
  catalogFilter?: string;
  soldFilter?: string;
  stripeFilter?: string;
  excludeFree?: boolean;
}

export function useFinancialSummary(
  societyId: string,
  filters: FinancialSummaryFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['financial-summary', societyId, filters],
    queryFn: async () => {
      const params: Record<string, string> = {};

      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.search) params.search = filters.search;
      if (filters.paymentMode) params.paymentMode = filters.paymentMode;
      if (filters.storeId) params.storeId = filters.storeId;
      if (filters.filterUserId) params.filterUserId = filters.filterUserId;
      if (filters.page !== undefined) params.page = String(filters.page);
      if (filters.limit) params.limit = String(filters.limit);
      if (filters.catalogFilter && filters.catalogFilter !== 'all') params.catalogFilter = filters.catalogFilter;
      if (filters.soldFilter && filters.soldFilter !== 'all') params.soldFilter = filters.soldFilter;
      if (filters.stripeFilter && filters.stripeFilter !== 'all') params.stripeFilter = filters.stripeFilter;
      if (filters.excludeFree) params.excludeFree = 'true';

      const { data } = await axiosInstance.get(
        `/pro-v2/societies/${societyId}/financial-summary`,
        { params },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!societyId,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });
}

// ═══════════════════════════════════════════════════════════════
// Hook: useRecapExport (on-demand, tous les contrats filtrés)
// ═══════════════════════════════════════════════════════════════

export function useRecapExport(
  societyId: string,
  filters: FinancialSummaryFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['recap-export', societyId, filters],
    queryFn: async () => {
      const params: Record<string, string> = {};

      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.search) params.search = filters.search;
      if (filters.paymentMode) params.paymentMode = filters.paymentMode;
      if (filters.storeId) params.storeId = filters.storeId;
      if (filters.filterUserId) params.filterUserId = filters.filterUserId;
      params.page = '0';
      params.limit = '100000';
      if (filters.catalogFilter && filters.catalogFilter !== 'all') params.catalogFilter = filters.catalogFilter;
      if (filters.soldFilter && filters.soldFilter !== 'all') params.soldFilter = filters.soldFilter;
      if (filters.stripeFilter && filters.stripeFilter !== 'all') params.stripeFilter = filters.stripeFilter;
      if (filters.excludeFree) params.excludeFree = 'true';

      const { data } = await axiosInstance.get(
        `/pro-v2/societies/${societyId}/financial-summary`,
        { params },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!societyId,
    staleTime: 0,
  });
}

// ═══════════════════════════════════════════════════════════════
// Hook: useFinancialCompiled
// ═══════════════════════════════════════════════════════════════

interface FinancialCompiledFilters {
  dateFrom?: string;
  dateTo?: string;
  storeId?: string;
  filterUserId?: string;
  retroDelayMonths?: number;
}

export function useFinancialCompiled(
  societyId: string,
  filters: FinancialCompiledFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['financial-compiled', societyId, filters],
    queryFn: async (): Promise<VendorFinancialCompiledData> => {
      const params: Record<string, string> = {};

      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.storeId) params.storeId = filters.storeId;
      if (filters.filterUserId) params.filterUserId = filters.filterUserId;
      if (filters.retroDelayMonths) params.retroDelayMonths = String(filters.retroDelayMonths);

      const { data } = await axiosInstance.get<VendorFinancialCompiledData>(
        `/pro-v2/societies/${societyId}/financial-compiled`,
        { params },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!societyId,
    staleTime: 5 * 60 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════
// Hook: useContractsExport
// ═══════════════════════════════════════════════════════════════

interface ExportFilters {
  dateFrom?: string;
  dateTo?: string;
  storeId?: string;
  filterUserId?: string;
}

export function useContractsExport(
  societyId: string,
  filters: ExportFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['contracts-export', societyId, filters],
    queryFn: async (): Promise<any[]> => {
      const params: Record<string, string> = {};
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.storeId) params.storeId = filters.storeId;
      if (filters.filterUserId) params.filterUserId = filters.filterUserId;

      const { data } = await axiosInstance.get(
        `/pro-v2/societies/${societyId}/contracts-export`,
        { params },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!societyId,
    staleTime: 5 * 60 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════
// Hook: useSavExport
// ═══════════════════════════════════════════════════════════════

export function useSavExport(
  societyId: string,
  filters: ExportFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['sav-export', societyId, filters],
    queryFn: async (): Promise<any[]> => {
      const params: Record<string, string> = {};
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.storeId) params.storeId = filters.storeId;
      if (filters.filterUserId) params.filterUserId = filters.filterUserId;

      const { data } = await axiosInstance.get(
        `/pro-v2/societies/${societyId}/sav-export`,
        { params },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!societyId,
    staleTime: 5 * 60 * 1000,
  });
}

// ═══════════════════════════════════════════════════════════════
// Hook: useProvisionExport
// ═══════════════════════════════════════════════════════════════

interface ProvisionExportFilters extends ExportFilters {
  warrantyName?: string;
}

export function useProvisionExport(
  societyId: string,
  filters: ProvisionExportFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['provision-export', societyId, filters],
    queryFn: async (): Promise<any[]> => {
      const params: Record<string, string> = {};
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.storeId) params.storeId = filters.storeId;
      if (filters.warrantyName) params.warrantyName = filters.warrantyName;

      const { data } = await axiosInstance.get(
        `/pro-v2/societies/${societyId}/provision-export`,
        { params },
      );
      return data;
    },
    enabled: options?.enabled !== false && !!societyId,
    staleTime: 5 * 60 * 1000,
  });
}
